/**
 * ユーザーメニュー（左下・ログイン中のみ・案1）。
 *
 * 右上の「公開中」アコーディオンと同じく、矢印で開閉を示す展開式メニュー。
 * - 閉時: 「[アバター] 名前 ▲」ボタン1つ（▲ = 展開できる合図）
 * - 開時: 子（チェックイン/プロフィール/ステータス/投稿の各トリガー）＋ログアウトを縦に展開
 *
 * 各操作コントロールは variant="menu" でメニュー項目用スタイル（.user-menu__item）の
 * トリガーボタンを描くので、本コンポーネントはそれらを children として並べるだけ。
 * 未ログイン時は何も出さない（ログイン導線は LoginControl が担当）。
 *
 * 閉じ方（タスク39・右上ツールバーと揃える）:
 * - ヘッダーの再クリック
 * - メニュー内モーダルが閉じたとき（成功後の自動クローズ・背景・閉じるボタンを問わない。userMenuContext.ts）
 * - 外側 pointerdown / ESC（モーダル表示中は無効＝モーダル側の操作を優先）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseAuthResult } from './useAuth';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { UserMenuContext } from './userMenuContext';
import './userMenu.css';

export interface UserMenuProps {
  /** App で一度だけ呼んだ useAuth の結果。 */
  readonly auth: UseAuthResult;
  readonly lang: Lang;
  /**
   * 左下表示の上書き（プロフィール編集を反映）。
   * displayName / avatarHidden 未指定なら Google アカウントの値を使う。
   */
  readonly profileOverride?: {
    displayName?: string;
    avatarHidden?: boolean;
  };
  /** メニュー項目（各操作コントロールのトリガー）。 */
  readonly children: React.ReactNode;
  /** トラッキング中か（ヘッダーに📡を出し、メニューを閉じていても動作中だと分かるようにする・タスク48）。 */
  readonly trackingActive?: boolean;
}

export function UserMenu({
  auth,
  lang,
  profileOverride,
  children,
  trackingActive = false,
}: UserMenuProps) {
  const { enabled, user, loading, logout } = auth;
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpenState] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);
  const m = messages(lang);

  // メニュー内モーダルが閉じたら、役目を終えたメニューも閉じる。
  const setModalOpen = useCallback((next: boolean) => {
    setModalOpenState(next);
    if (!next) {
      setOpen(false);
    }
  }, []);
  const ctxValue = useMemo(() => ({ setModalOpen }), [setModalOpen]);

  // 外側クリックと ESC で閉じる（MapToolbar.tsx と同じ方式）。メニューだけが開いている間だけ listener を張る。
  // モーダル表示中は張らない（ESC や背景クリックでメニューごとモーダルを消し、入力中の内容を失わないように）。
  // なお overlay は position: fixed だが DOM 上は .user-menu の子孫なので、contains 判定では「内側」になる。
  // mousedown ではなく pointerdown なのは、地図キャンバスがタッチ操作で touchstart を preventDefault するため、
  // 端末によってはエミュレートされた mousedown が来ず「地図をタップしても閉じない」が起きるため。
  useEffect(() => {
    if (!open || modalOpen) {
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      // 閉じたらヘッダーへフォーカスを戻す（キーボード操作で迷子にならないように）
      setOpen(false);
      headerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, modalOpen]);

  // 未設定・初期化中・未ログインは出さない（ログインボタンは LoginControl 側）。
  if (!enabled || loading || !user) {
    return null;
  }

  const displayName = profileOverride?.displayName ?? user.name;
  const showPicture = !profileOverride?.avatarHidden && !!user.picture;
  const initial = displayName.slice(0, 1);

  return (
    <div className="user-menu" ref={rootRef}>
      {/* 展開パネル（開いている時だけ・ヘッダーの上に積む） */}
      {open && (
        <div className="user-menu__panel">
          <UserMenuContext.Provider value={ctxValue}>
            {children}
          </UserMenuContext.Provider>
          <button
            type="button"
            className="user-menu__item user-menu__logout"
            onClick={() => logout()}
          >
            ↩️ {m.menu.logout}
          </button>
        </div>
      )}

      {/* ヘッダー（常時・アバター＋名前＋矢印）。クリックで開閉。 */}
      <button
        type="button"
        ref={headerRef}
        className="user-menu__header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={user.email ?? displayName}
      >
        {showPicture ? (
          <img
            className="user-menu__avatar"
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="user-menu__avatar user-menu__avatar--initial">
            {initial}
          </span>
        )}
        <span className="user-menu__name">{displayName}</span>
        {trackingActive && (
          <span
            className="user-menu__tracking"
            title={m.menu.trackingBadge}
            aria-label={m.menu.trackingBadge}
          >
            📡
          </span>
        )}
        <span
          className={`user-menu__caret${open ? ' user-menu__caret--open' : ''}`}
          aria-hidden="true"
        >
          ▲
        </span>
      </button>
    </div>
  );
}
